import { useId, useState } from 'react';
import { toast } from 'sonner';
import { AlertCircle, KeyRound, Pencil } from 'lucide-react';
import { Button } from '../ui/Button';
import { ConfirmModal } from '../ui/ConfirmModal';
import { useCurrentUser } from '../../hooks/useAuth';
import { api, ApiError } from '../../lib/api';
import type { AdminPersonResponse, PersonApi } from '../../routes/admin/person/types';

/**
 * Work email field with an admin-only override.
 *
 * The address is the SSO identity key, not an ordinary profile field, so
 * it stays read-only in the details form: the override unlocks it on its
 * own, saves through its own route, and confirms the before/after first.
 * The case it exists for is a record created with a mistyped address, so
 * Entra hands back a name that matches nobody and provisions a duplicate.
 */

const INPUT_CLASSES =
  'mt-1.5 block h-10 w-full rounded-md border border-line-strong/20 bg-surface px-3 text-sm ' +
  'shadow-sm focus:border-accent focus:outline-none focus:ring-2 focus:ring-line/15 ' +
  'disabled:bg-surface-2 disabled:text-fg-muted';

export function WorkEmailOverride({
  person,
  onChanged,
}: {
  person: PersonApi;
  /** Fired with the updated person after a successful change. */
  onChanged: (p: PersonApi) => void;
}) {
  const { data: user } = useCurrentUser();
  const inputId = useId();
  const [unlocked, setUnlocked] = useState(false);
  const [value, setValue] = useState(person.email);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canOverride =
    user?.roles.some((r) => r === 'admin' || r === 'super_admin') ?? false;
  const next = value.trim().toLowerCase();
  const changed = next.length > 0 && next !== person.email.toLowerCase();

  async function save(reason?: string) {
    setError(null);
    try {
      const r = await api<AdminPersonResponse & { account_updated: boolean }>(
        `/api/admin/people/${person.id}/email`,
        { method: 'PUT', body: JSON.stringify({ email: next, reason }) },
      );
      setConfirming(false);
      setUnlocked(false);
      onChanged(r.person);
      toast.success(
        r.account_updated
          ? `Work email changed to ${r.person.email}. Their sign-in account moved with it.`
          : `Work email changed to ${r.person.email}. No sign-in account existed yet; SSO will create one.`,
      );
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'change failed';
      setConfirming(false);
      setError(msg);
      toast.error(msg);
    }
  }

  return (
    // A plain container rather than the form's label-wraps-input pattern:
    // the override button sits in the label row, and inside a <label> a
    // click on it would be forwarded to the input.
    <div className="text-sm">
      <div className="flex min-h-[1.75rem] items-center justify-between gap-2">
        <label htmlFor={inputId} className="font-medium text-fg-muted">
          Work email
        </label>
        {canOverride && !unlocked ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconLeft={<Pencil className="h-3.5 w-3.5" />}
            onClick={() => {
              setValue(person.email);
              setError(null);
              setUnlocked(true);
            }}
          >
            Change
          </Button>
        ) : null}
      </div>

      <input
        id={inputId}
        type="text"
        inputMode="email"
        autoComplete="off"
        value={unlocked ? value : person.email}
        disabled={!unlocked}
        onChange={(e) => setValue(e.target.value)}
        className={INPUT_CLASSES}
      />

      {unlocked ? (
        <div className="mt-2 space-y-2">
          <p className="flex items-start gap-1.5 text-xs text-warning-soft-fg">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              This is the address single sign-on matches on. Changing it moves
              their existing sign-in account and signs them out of any open
              session.
            </span>
          </p>
          {error ? (
            <p className="flex items-start gap-1.5 text-xs text-danger-soft-fg">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="warning"
              size="sm"
              disabled={!changed}
              onClick={() => setConfirming(true)}
            >
              Change email
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setUnlocked(false);
                setValue(person.email);
                setError(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={confirming}
        tone="warning"
        icon={KeyRound}
        title="Change work email"
        confirmLabel="Change email"
        reasonLabel="Reason (optional)"
        reasonPlaceholder="e.g. created with a typo, corrected to match Entra"
        message={
          <span className="block space-y-1">
            <span className="block">
              {person.full_name} will sign in as <strong>{next}</strong> instead
              of <strong>{person.email}</strong>.
            </span>
            <span className="block text-fg-muted">
              Their record, onboarding and history are untouched. Anything
              already sent to another system (SAP, Planon) keeps the old address
              until it is pushed again.
            </span>
          </span>
        }
        onConfirm={(reason) => save(reason)}
        onClose={() => setConfirming(false)}
      />
    </div>
  );
}
